import { serializeError } from "serialize-error";

import awsconfig from "../aws-exports";
import { API, graphqlOperation } from "aws-amplify";
import { createHistoryNote, createLog } from "../graphql/mutations";
import { updateReloadYard } from "../graphql/reloadyard/custom-mutations";

import {
  APP_VERSION,
  AreaType,
  AppType,
  ERROR_MSG,
  LogStatus,
  PortalType,
  ReloadYardStatus,
  SubscriptionStatus,
  UserAclOptions,
  UserType,
} from "./constants";

const _ = require("lodash");

// Global logout function - set by App.js
let globalLogoutFunction = null;

// Token refresh tracking
let isTokenRefreshing = false;
let tokenRefreshPromise = null;
let tokenRefreshResolve = null;
let tokenRefreshReject = null;

/**
 * Set the global logout function to be called on 401 errors
 * @param {Function} logoutFn - Function to call when 401 is detected
 */
export const setGlobalLogoutFunction = (logoutFn) => {
  globalLogoutFunction = logoutFn;
};

/**
 * Mark that token refresh has started
 * @returns {Promise} Promise that resolves when refresh completes
 */
export const startTokenRefresh = () => {
  if (!isTokenRefreshing) {
    isTokenRefreshing = true;
    tokenRefreshPromise = new Promise((resolve, reject) => {
      tokenRefreshResolve = resolve;
      tokenRefreshReject = reject;
    });
    console.log(new Date().toISOString(), "Token refresh started - tracking in progress");
  }
  return tokenRefreshPromise;
};

/**
 * Mark that token refresh has completed successfully
 */
export const completeTokenRefresh = () => {
  if (isTokenRefreshing && tokenRefreshResolve) {
    console.log(new Date().toISOString(), "Token refresh completed successfully");
    isTokenRefreshing = false;
    tokenRefreshResolve();
    tokenRefreshPromise = null;
    tokenRefreshResolve = null;
    tokenRefreshReject = null;
  }
};

/**
 * Mark that token refresh has failed
 */
export const failTokenRefresh = (error) => {
  if (isTokenRefreshing && tokenRefreshReject) {
    console.error("Token refresh failed", error);
    isTokenRefreshing = false;
    tokenRefreshReject(error);
    tokenRefreshPromise = null;
    tokenRefreshResolve = null;
    tokenRefreshReject = null;
  }
};

/**
 * Check if token refresh is currently in progress
 * @returns {boolean} True if refresh is in progress
 */
export const isTokenRefreshInProgress = () => {
  return isTokenRefreshing;
};

/**
 * Wait for token refresh to complete (if in progress)
 * @returns {Promise} Promise that resolves when refresh completes or immediately if not refreshing
 */
export const waitForTokenRefresh = async () => {
  if (isTokenRefreshing && tokenRefreshPromise) {
    try {
      await tokenRefreshPromise;
    } catch (error) {
      // Refresh failed, but we'll handle logout separately
      console.error("Token refresh promise rejected:", error);
    }
  }
};

/**
 * Precise phrases emitted by JWT verification libraries ONLY when a token
 * has literally expired.  They cannot appear for permission/ACL errors, so
 * when the server sends one of these we trust it unconditionally — this
 * handles clock-skew cases where the client OIDC timer hasn't caught up yet.
 */
const SERVER_EXPIRY_PHRASES = [
  "token has expired",
  "token expired",   // spaced variant emitted by many OIDC providers / custom backends
  "jwt expired",
  "tokenexpired",    // unspaced variant (some libraries omit the space)
  "token is expired",
];

/**
 * Check if error signals that the JWT/refresh token has actually expired.
 * This deliberately excludes generic permission/ACL 401s to avoid logging
 * the user out when they simply lack access to a specific resource.
 *
 * Detection has two tiers:
 *
 *  UNAMBIGUOUS — server message contains a precise expiry phrase
 *    → trust the server, bypass the client-side OIDC clock check.
 *      This handles clock-skew where user.expired is still false on the client
 *      but the server has already rejected the token.
 *
 *  AMBIGUOUS — HTTP 401, UnauthorizedException, or NotAuthorizedException
 *    → cross-check with window.isAuthTokenExpired() to avoid logging out on
 *      pure permission/ACL errors that also return 401.
 */
const isUnauthorizedError = (error) => {
  // Ignore during app initialisation (no token yet)
  const hasToken = window.getAuthToken && window.getAuthToken();
  if (!hasToken) {
    if (
      error?.response?.status === 401 ||
      error?.status === 401
    ) {
      console.warn(
        "401 error but no token available - likely during initialisation. Ignoring.",
      );
    }
    return false;
  }

  // --- Tier classification ---

  let looksLikeTokenExpiry = false;
  // True when the server used an expiry-specific phrase → bypass OIDC cross-check
  let serverExplicitlyReportsExpiry = false;

  const hasExpiryPhrase = (str) =>
    SERVER_EXPIRY_PHRASES.some((p) => str.includes(p));

  // HTTP 401 — ambiguous (permission errors also return 401)
  if (error?.response?.status === 401 || error?.status === 401) {
    looksLikeTokenExpiry = true;
  }

  // AppSync / GraphQL errors.
  // Scanned unconditionally — a 401 response may ALSO carry an explicit expiry
  // phrase in error.errors (e.g. AppSync returns HTTP 401 + "jwt expired" body).
  // Skipping this block when looksLikeTokenExpiry is already true would prevent
  // upgrading an ambiguous 401 to an unambiguous expiry signal (clock-skew fix).
  if (error?.errors && Array.isArray(error.errors)) {
    for (const err of error.errors) {
      const type = err.errorType ?? "";
      const msg = (err.message ?? "").toLowerCase();

      if (hasExpiryPhrase(msg)) {
        // Unambiguous — server is explicit; upgrade and stop scanning
        looksLikeTokenExpiry = true;
        serverExplicitlyReportsExpiry = true;
        break;
      }
      // UnauthorizedException: AppSync authorization failure (ambiguous — also used for ACL)
      // NotAuthorizedException: Cognito token invalid/expired (ambiguous — also used for bad creds)
      if (type === "UnauthorizedException" || type === "NotAuthorizedException") {
        looksLikeTokenExpiry = true;
        // keep scanning — a later error entry might contain an explicit expiry phrase
      }
    }
  }

  // Top-level error message from REST / fetch wrappers.
  // Also scanned unconditionally for the same reason as above: a 401 that also
  // carries an expiry phrase in error.message must be upgraded to unambiguous.
  if (!serverExplicitlyReportsExpiry && error?.message) {
    const msg = error.message.toLowerCase();
    if (hasExpiryPhrase(msg)) {
      looksLikeTokenExpiry = true;
      serverExplicitlyReportsExpiry = true;
    }
  }

  if (!looksLikeTokenExpiry) {
    return false;
  }

  // Unambiguous: server explicitly said the token is expired — trust it even
  // if the local OIDC clock hasn't caught up (clock skew).
  if (serverExplicitlyReportsExpiry) {
    return true;
  }

  // Ambiguous: cross-check with local OIDC state to avoid logging out on
  // permission errors.  Default to false (not expired) when the helper is not
  // yet available so initialisation-time 401s are not treated as expiry.
  const tokenExpired =
    typeof window.isAuthTokenExpired === "function"
      ? window.isAuthTokenExpired()
      : false;

  if (!tokenExpired) {
    console.warn(
      "401 received but OIDC token is not expired — treating as a permission error, not token expiry. Logout suppressed.",
      error,
    );
    return false;
  }

  return true;
};

/**
 * Handle 401 errors by calling logout (unless token refresh is in progress)
 * @param {*} error - Error object
 * @returns {boolean|Promise<boolean>} - True if error was a 401 and was handled, or a promise that resolves to this value
 */
export const handleUnauthorizedError = (error) => {
  if (!isUnauthorizedError(error)) {
    return false;
  }

  // If token refresh is in progress, return a promise that waits for refresh
  if (isTokenRefreshing) {
    console.log(new Date().toISOString(),
      "401 error detected during token refresh - waiting for refresh to complete...",
    );
    return waitForTokenRefresh()
      .then(() => {
        // After refresh completes, check if we still have a valid token
        const hasToken = window.getAuthToken && window.getAuthToken();
        if (hasToken) {
          console.log(new Date().toISOString(),
            "Token refresh completed successfully - request should be retried",
          );
          // Return false to indicate error was not handled (so request can be retried)
          return false;
        }
        // If no token after refresh, refresh must have failed - logout
        console.error(
          "No token after refresh - refresh must have failed. Logging out...",
        );
        if (globalLogoutFunction) {
          globalLogoutFunction();
        }
        return true; // Error was handled (logout)
      })
      .catch((refreshError) => {
        console.error("Token refresh failed:", refreshError);
        // Refresh failed, proceed with logout
        if (globalLogoutFunction) {
          globalLogoutFunction();
        }
        return true; // Error was handled (logout)
      });
  }

  // No refresh in progress - logout immediately
  console.error("401 Unauthorized error detected. Logging out...", error);
  if (globalLogoutFunction) {
    globalLogoutFunction();
  } else {
    console.warn("Global logout function not set. Cannot logout on 401 error.");
  }
  return true; // Indicates error was handled
};

/**
 * True when an AppSync/DynamoDB error is a condition-expression failure — the
 * row changed underneath a conditioned write, so the newer write wins and the
 * caller usually retries or drops the update rather than reporting an error.
 * AppSync surfaces it inconsistently (errorType
 * "DynamoDB:ConditionalCheckFailedException" from some resolvers, message text
 * from others), so both are checked.
 */
export const isConditionalCheckFailed = (error) => {
  const type = error?.errors?.[0]?.errorType || "";
  const message = error?.errors?.[0]?.message || error?.message || "";
  return `${type} ${message}`.includes("ConditionalCheckFailed");
};

export const logError = (msg, error, notify, redirect) => {
  try {
    // Only skip logging when a synchronous true is returned, meaning logout was
    // triggered immediately (genuine token expiry, no refresh in progress).
    // A Promise return means a refresh is in progress; the auth lifecycle is
    // already handled by the API interceptors, so we still proceed to log the
    // error — the createLog call will be retried by the interceptor after refresh.
    if (handleUnauthorizedError(error) === true) {
      return;
    }

    let errorObj;
    let exception;
    try {
      errorObj = JSON.stringify(serializeError(error?.response || error));
      exception = errorObj;
    } catch (err) {
      if (error.errors) {
        errorObj = error.errors.map((itm) => itm.message);
      } else errorObj = error.message;
      exception = String(errorObj);
    }
    const isNetwork = errorObj.includes("Network Error");
    const errorMessage = isNetwork
      ? "Network Error! Restore internet connection and hit REFRESH."
      : ERROR_MSG;

    console.error("ERROR:", msg, errorObj);
    if (notify) notify(errorMessage, "error");

    API.graphql(
      graphqlOperation(createLog, {
        input: {
          status: LogStatus.NEW,
          user: window.appUser?.email,
          appType: window.appUser?.appType,
          tenantId: window.appUser?.reloadYardId
            ? window.appUser.reloadYardId
            : "ADMIN",
          message: msg,
          exception,
          appVersion: APP_VERSION,
          url: window.location?.pathname,
          context: window.navigator?.userAgent,
        },
      }),
    );

    if (isNetwork && redirect) redirect("/networkError");
  } catch (err) {
    // Silently fail if error logging itself fails to prevent infinite loops
  }
};

export const logHistory = (historyNote) => {
  // Use a more robust approach for background execution
  // This ensures the history logging continues even if the user navigates away

  const historyNoteInput = {
    ...historyNote,
    groups: window.appUser?.groups,
    reloadYardId: window.appUser?.reloadYardId,
    createdBy: window.appUser?.fullName,
    dateTime: new Date().toISOString(),
  };

  // Create a promise that we don't await
  const historyPromise = API.graphql(
    graphqlOperation(createHistoryNote, { input: historyNoteInput }),
  ).catch((error) => {
    logError("appUtils:logHistory", error, false);
  });

  // Store the promise in a way that prevents it from being garbage collected
  // This ensures the GraphQL call completes even after navigation
  if (typeof window !== "undefined") {
    // Store the promise on window to prevent cleanup during navigation
    if (!window._pendingHistoryLogs) {
      window._pendingHistoryLogs = [];
    }
    window._pendingHistoryLogs.push(historyPromise);

    // Clean up completed promises to prevent memory leaks
    historyPromise.finally(() => {
      if (window._pendingHistoryLogs) {
        const index = window._pendingHistoryLogs.indexOf(historyPromise);
        if (index > -1) {
          window._pendingHistoryLogs.splice(index, 1);
        }
      }
    });
  }

  return historyPromise; // Return promise for cases where caller might want to track it
};

// Utility function to check pending history logs (useful for debugging)
export const getPendingHistoryLogs = () => {
  if (typeof window !== "undefined" && window._pendingHistoryLogs) {
    return window._pendingHistoryLogs.length;
  }
  return 0;
};

export const getEnv = () => {
  let env = "dev";

  try {
    let bucket = API._options.aws_user_files_s3_bucket;
    if (bucket) {
      if (bucket.endsWith("prod")) env = "prod";
      if (bucket.endsWith("hotfix")) env = "hotfix";
    } else {
      bucket = awsconfig.aws_user_files_s3_bucket;
      if (bucket.endsWith("prod")) env = "prod";
      if (bucket.endsWith("hotfix")) env = "hotfix";
    }
  } catch (err) {
    logError("utils:common:getEnv", err, false);
  }

  return env;
};

export const isTWA = () => {
  return window.location.host.toLowerCase().includes("twa");
};

/** Which portal the viewer is working: the Manager Portal or the Operator App.
 * A supervisor and a manager switch between the two from the user menu, which
 * stores the choice; crew and the Android wrapper are always the Operator App.
 *
 * ManagerLayout seeds its portal state from this and re-stores it on every
 * switch, so a page rendered under that layout reads the same portal the menu
 * beside it is showing — which is what decides where the Gate Queue's Open
 * button leads: the shipment's edit form in the Manager Portal, the crew
 * process page in the Operator App. */
export const getActivePortalType = () => {
  if (window.appUser?.type === UserType.CREW) return PortalType.CREW;
  if (isTWA()) return PortalType.CREW;
  return localStorage.getItem("portalType") || PortalType.MANAGER;
};

export const setupLocationMaps = (reloadYard) => {
  reloadYard.areas.items = reloadYard.areas.items.filter(
    (itm) => itm.active === "true",
  );
  const areaMap = {};
  reloadYard.areas.items.forEach((itm) => (areaMap[itm.id] = itm));
  reloadYard.areaMap = areaMap;
  const cachedTrackAreaId = reloadYard.trackArea?.id;
  const cachedTrackArea = cachedTrackAreaId
    ? areaMap[cachedTrackAreaId]
    : null;
  reloadYard.trackArea =
    (cachedTrackArea?.areaType === AreaType.TRACK ? cachedTrackArea : null) ||
    reloadYard.areas.items.find((area) => area.areaType === AreaType.TRACK) ||
    null;
};

export const setupCommodityMaps = (reloadYard) => {
  reloadYard.yardCommodities.items = _.orderBy(
    reloadYard.yardCommodities.items,
    ["sortKey"],
    ["asc"],
  );
  reloadYard.activeYardCommodities = {
    items: reloadYard.yardCommodities.items.filter(
      (itm) => itm.active === "true",
    ),
  };
  const commodityMap = {};
  reloadYard.yardCommodities.items.forEach(
    (itm) => (commodityMap[itm.id] = itm),
  );
  reloadYard.yardCommodityMap = commodityMap;
};

export const getReloadYardStatus = (reloadYard, subscription) => {
  const oldYardStatus = reloadYard.status;
  let newYardStatus = reloadYard.status;
  if (subscription.status === SubscriptionStatus.ACTIVE)
    newYardStatus = ReloadYardStatus.ACTIVE;
  else if (subscription.status === SubscriptionStatus.CANCELED)
    newYardStatus = ReloadYardStatus.CANCELLED;
  else if (subscription.status === SubscriptionStatus.TRIALING)
    newYardStatus = ReloadYardStatus.TRIALING;

  // Do not override manual suspension or termination
  const effectiveYardStatus =
    oldYardStatus === ReloadYardStatus.SUSPENDED ||
      oldYardStatus === ReloadYardStatus.TERMINATED
      ? oldYardStatus
      : newYardStatus;

  return effectiveYardStatus;
};

export const retrieveStripeSubscription = async (reloadYard) => {
  try {
    const response = await API.post(
      "transREST",
      "/stripe-retrieve-subscription",
      {
        body: { subscriptionId: reloadYard.subscription.subscriptionId },
      },
    );

    if (response.error) {
      logError(
        "utils:common:retrieveStripeSubscription:response",
        response.error,
        false,
      );
      return reloadYard.subscription;
    }

    if (
      response.status !== reloadYard.subscription.status ||
      response.id !== reloadYard.subscription.subscriptionId ||
      response.plan?.id !== reloadYard.subscription.priceId ||
      response.plan?.nickname !== reloadYard.subscription.name
    ) {
      const updatedSubscription = {
        ...reloadYard.subscription,
        status: response.status,
      };
      if (response.id) updatedSubscription.subscriptionId = response.id;
      if (response.plan?.id) updatedSubscription.priceId = response.plan.id;
      if (response.plan?.nickname)
        updatedSubscription.name = response.plan.nickname;

      const newStatus = getReloadYardStatus(reloadYard, updatedSubscription);

      API.graphql(
        graphqlOperation(updateReloadYard, {
          input: {
            id: reloadYard.id,
            status: newStatus,
            subscription: { ...updatedSubscription },
          },
        }),
      );
    }

    return response;
  } catch (error) {
    logError("utils:common:retrieveStripeSubscription", error, false);
    return reloadYard.subscription;
  }
};

export const isSubscriptionDisabled = (appUser) => {
  const user = appUser || window.appUser;
  const reloadYardStatus = user?.reloadYard?.status;
  const subscriptionStatus = user?.subscription?.status;
  return (
    subscriptionStatus === SubscriptionStatus.CANCELED ||
    subscriptionStatus === SubscriptionStatus.UNPAID ||
    reloadYardStatus === ReloadYardStatus.SUSPENDED ||
    reloadYardStatus === ReloadYardStatus.TERMINATED
  );
};

export const getIndexKeyValue = (column, value) => {
  if (value) return value;
  else return "${ctx.args.input." + column + "}";
};

export const isAdmin = () => window.appUser?.appType === AppType.ADMIN;

export const isAuthorized = (permission) => {
  const userType = window.appUser.type;
  if (userType === UserType.MANAGER || userType === UserType.ADMIN) {
    return true;
  }
  return window.appUser.permissionList[permission];
};

export const getUserTypeAcls = (userType) => {
  return UserAclOptions.filter((acl) => acl.roles[userType]);
};

export const refreshApp = async () => {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch (err) {
    // Silently fail cache clearing - not critical for app functionality
  }

  window.location.href = window.location.origin;
};

/**
 * Wrapper for API.graphql that handles 401 errors
 * Use this instead of direct API.graphql calls
 */
export const safeGraphQL = async (...args) => {
  try {
    const result = await API.graphql(...args);
    return result;
  } catch (error) {
    const handled = handleUnauthorizedError(error);
    // A Promise return means a refresh was in progress (only case where retry makes sense).
    // A synchronous false means a permission error — must not retry.
    const refreshWasInProgress = handled instanceof Promise;
    const wasHandled = refreshWasInProgress ? await handled : handled;

    if (wasHandled) {
      throw new Error("Unauthorized - Session expired");
    }

    // Only retry if the refresh we waited for completed successfully.
    // Do NOT check isTokenRefreshInProgress() — a new renewal cycle may have started
    // immediately after the previous one finished; the freshly-issued token is still valid.
    if (refreshWasInProgress && !wasHandled) {
      console.log(new Date().toISOString(), "Retrying safeGraphQL request after token refresh...");
      try {
        return await API.graphql(...args);
      } catch (retryError) {
        const retryHandled = handleUnauthorizedError(retryError);
        const retryWasHandled =
          retryHandled instanceof Promise ? await retryHandled : retryHandled;
        if (retryWasHandled) {
          throw new Error("Unauthorized - Session expired");
        }
        throw retryError;
      }
    }

    throw error;
  }
};

/**
 * Helper function to retry API calls after token refresh
 */
const retryAPICall = async (apiMethod, args, error) => {
  const handled = handleUnauthorizedError(error);
  // A Promise return means a refresh was in progress (only case where retry makes sense).
  // A synchronous false means a permission error — must not retry.
  const refreshWasInProgress = handled instanceof Promise;
  const wasHandled = refreshWasInProgress ? await handled : handled;

  if (wasHandled) {
    throw new Error("Unauthorized - Session expired");
  }

  // Only retry if the refresh we waited for completed successfully.
  // Do NOT check isTokenRefreshInProgress() — see safeGraphQL comment.
  if (refreshWasInProgress && !wasHandled) {
    console.log(new Date().toISOString(), `Retrying ${apiMethod.name} request after token refresh...`);
    try {
      return await apiMethod(...args);
    } catch (retryError) {
      const retryHandled = handleUnauthorizedError(retryError);
      const retryWasHandled =
        retryHandled instanceof Promise ? await retryHandled : retryHandled;
      if (retryWasHandled) {
        throw new Error("Unauthorized - Session expired");
      }
      throw retryError;
    }
  }

  throw error;
};

/**
 * Wrapper for API.post/get/put/delete that handles 401 errors
 * Use this instead of direct API.post/get/put/delete calls
 */
export const safeAPI = {
  post: async (...args) => {
    try {
      const result = await API.post(...args);
      return result;
    } catch (error) {
      return retryAPICall(API.post, args, error);
    }
  },
  get: async (...args) => {
    try {
      const result = await API.get(...args);
      return result;
    } catch (error) {
      return retryAPICall(API.get, args, error);
    }
  },
  put: async (...args) => {
    try {
      const result = await API.put(...args);
      return result;
    } catch (error) {
      return retryAPICall(API.put, args, error);
    }
  },
  del: async (...args) => {
    try {
      const result = await API.del(...args);
      return result;
    } catch (error) {
      return retryAPICall(API.del, args, error);
    }
  },
};
/* Retries an operation with exponential backoff
 * @param {Function} operation - Async function that returns a promise
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.retryDelay - Initial retry delay in ms (default: 1000)
 * @param {Function} options.onRetry - Callback called on each retry attempt
 * @returns {Promise<any>} Result of the operation
 */
export const retryWithBackoff = async (operation, options = {}) => {
  const { maxRetries = 3, retryDelay = 1000, onRetry = null } = options;
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Check if error is retryable (Throttling, Network Error, etc.)
      const isRetryable =
        error?.errors?.some(
          (e) =>
            e?.errorType === "ProvisionedThroughputExceededException" ||
            e?.errorType === "ThrottlingException",
        ) ||
        error?.message?.includes("Network Error") ||
        error?.message?.includes("timeout") ||
        error?.networkError;

      if (attempt < maxRetries && isRetryable) {
        if (onRetry) onRetry(attempt, error);
        // Exponential backoff: delay * 2^attempt
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

/**
 * Batches mutations with retry logic to avoid overwhelming the backend
 * @param {Array} operations - Array of async functions that return promises
 * @param {Object} options - Configuration options
 * @param {number} options.batchSize - Number of operations per batch (default: 10)
 * @param {number} options.maxRetries - Maximum number of retries per operation (default: 3)
 * @param {number} options.retryDelay - Initial retry delay in ms (default: 1000)
 * @param {number} options.batchDelay - Delay between batches in ms (default: 100)
 * @returns {Promise<Array>} Array of results with { success: boolean, result?: any, error?: any, index: number }
 */
export const batchMutations = async (operations, options = {}) => {
  const {
    batchSize = 100,
    maxRetries = 3,
    retryDelay = 1000,
    batchDelay = 0,
  } = options;
  debugger;

  if (!operations || operations.length === 0) {
    return [];
  }

  const results = [];
  const batches = [];

  // Split operations into batches
  for (let i = 0; i < operations.length; i += batchSize) {
    batches.push(operations.slice(i, i + batchSize));
  }

  // Process batches sequentially
  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    const batchResults = await Promise.allSettled(
      batch.map(async (operation, operationIndex) => {
        const globalIndex = batchIndex * batchSize + operationIndex;
        let lastError;

        // Retry logic with exponential backoff
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const result = await operation();
            debugger;

            // Check for GraphQL errors in response body (GraphQL can return HTTP 200 with errors)
            // AWS Amplify wraps the response, so check both result.errors and result.data?.errors
            const hasGraphQLErrors =
              result?.errors?.length > 0 || result?.data?.errors?.length > 0;

            if (hasGraphQLErrors) {
              const graphQLErrors = result.errors || result.data.errors;
              const errorMessage = graphQLErrors
                .map((e) => e.message || JSON.stringify(e))
                .join("; ");

              // Create error object that preserves the original GraphQL error structure
              const graphQLError = new Error(`GraphQL errors: ${errorMessage}`);
              graphQLError.errors = graphQLErrors; // Preserve original errors array
              graphQLError.response = result; // Preserve full response for debugging
              lastError = graphQLError;

              // Check if error is retryable
              const isRetryable = graphQLErrors.some(
                (e) =>
                  e?.errorType === "ProvisionedThroughputExceededException" ||
                  e?.errorType === "ThrottlingException",
              );

              if (attempt < maxRetries && isRetryable) {
                // Exponential backoff: delay * 2^attempt
                const delay = retryDelay * Math.pow(2, attempt);
                await new Promise((resolve) => setTimeout(resolve, delay));
                continue;
              }

              // Non-retryable GraphQL error or max retries reached
              return {
                success: false,
                error: lastError,
                index: globalIndex,
              };
            }

            return { success: true, result, index: globalIndex };
          } catch (error) {
            lastError = error;

            // Check if error is retryable (not a validation error, etc.)
            const isRetryable =
              error?.errors?.some(
                (e) =>
                  e?.errorType === "ProvisionedThroughputExceededException" ||
                  e?.errorType === "ThrottlingException",
              ) ||
              error?.message?.includes("Network Error") ||
              error?.message?.includes("timeout") ||
              error?.networkError;

            if (attempt < maxRetries && isRetryable) {
              // Exponential backoff: delay * 2^attempt
              const delay = retryDelay * Math.pow(2, attempt);
              await new Promise((resolve) => setTimeout(resolve, delay));
              continue;
            }

            // Non-retryable error or max retries reached
            return {
              success: false,
              error: lastError,
              index: globalIndex,
            };
          }
        }

        return {
          success: false,
          error: lastError,
          index: globalIndex,
        };
      }),
    );

    // Process batch results
    batchResults.forEach((settled, idx) => {
      if (settled.status === "fulfilled") {
        results.push(settled.value);
      } else {
        const globalIndex = batchIndex * batchSize + idx;
        results.push({
          success: false,
          error: settled.reason,
          index: globalIndex,
        });
      }
    });

    // Delay between batches to avoid overwhelming the backend
    if (batchIndex < batches.length - 1 && batchDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, batchDelay));
    }
  }

  return results;
};

/**
 * Every row a paginated AppSync list or index query can return, not just the
 * first page.
 *
 * A `limit` is a page size, never a total: DynamoDB stops at the limit and
 * hands back a nextToken, so a query written with a limit and no loop silently
 * reports a short answer as if it were the whole one. It is worse with a
 * `filter`, which DynamoDB applies AFTER reading the page — a filtered query
 * can return an empty page and a nextToken while the rows it was asked for sit
 * on the next page.
 *
 * @param {object} args
 * @param {string} args.query the GraphQL document
 * @param {object} [args.variables] everything except limit and nextToken
 * @param {string} args.responseKey the field under `data` holding items/nextToken
 * @param {number} [args.pageLimit=100] rows per round trip
 * @param {number} [args.maxPages=20] round trips before the read gives up
 * @param {string} [args.label] names the read in the truncation warning
 * @param {boolean} [args.throwOnTruncation=false] throw instead of warning
 *   when the read stops with rows unread — for callers that bill or invoice
 *   from the result and must not act on a silent partial answer
 * @returns {Promise<Array>} the items from every page read
 */
export const fetchAllPages = async ({
  query,
  variables = {},
  responseKey,
  pageLimit = 100,
  maxPages = 20,
  label,
  throwOnTruncation = false,
}) => {
  const items = [];
  let nextToken = null;
  let pages = 0;

  do {
    const response = await API.graphql(
      graphqlOperation(query, { ...variables, limit: pageLimit, nextToken }),
    );
    const page = response?.data?.[responseKey];
    if (page?.items?.length) items.push(...page.items.filter(Boolean));
    nextToken = page?.nextToken || null;
    pages += 1;
  } while (nextToken && pages < maxPages);

  // The cap is a runaway guard, not a page budget — reaching it means the
  // caller is holding an incomplete list, which is exactly the thing this
  // helper exists to prevent, so it says so. Warned rather than logged through
  // logError: callers include pages that poll, and a log row every tick would
  // bury the condition it is reporting.
  if (nextToken) {
    const message = `fetchAllPages(${
      label || responseKey
    }): stopped after ${pages} pages (${
      items.length
    } rows); more rows were left unread.`;
    if (throwOnTruncation) throw new Error(message);
    console.warn(message);
  }

  return items;
};
